import { cloneDeep, get, set } from 'lodash';
import { VN_Kalkulation_Personalausgaben_Kreativwirtschaft_COMPONENT_PATHS } from '../../../../../lib/constants/componentsPath';
import {
	ANTRAGS_FORMULAREN,
	ART_DER_EIGENLEISTUNG,
	KOSTEN_ARTEN,
	KOSTEN_POSITION,
	MONAT_LISTE,
	ZAHLUNGSWEISE,
} from '../../../../../lib/constants/constant';
import { getFieldNamefromDataPath, getIDFromResponseOption, groupKostenByPosition, isTrue } from '../../../../../utils/helper';
import { executeEmobLisVNFormulas } from './emobLisVNFormulas';

// import schemas
import LeistungsGruppeSchema from '../../../../../lib/paths/leistungsgruppe_FULL_Paths.json';
import NachweisPositionsSchema from '../../../../../lib/paths/nachweisposition_FULL_Paths.json';
import VerwendungsnachweisSchema from '../../../../../lib/paths/verwendungsnachweis_FULL_Paths.json';
const VN_P = VerwendungsnachweisSchema.attributes;
const LG_P = LeistungsGruppeSchema.attributes;
const NP_P = NachweisPositionsSchema.attributes;

export const executeVerwendungsnachweisFormulas = (
	application: any,
	kostenKategories: any,
	leistungsgruppe: any,
	alleVerwendungsnachweise?: any[]
) => {
	const antragsFormular = get(application, VN_P.Foerderantrag.schema.attributes.Allgemein_Antragsformular.path, '');

	switch (antragsFormular) {
		case ANTRAGS_FORMULAREN.EmobLis:
			return executeEmobLisVNFormulas(application, alleVerwendungsnachweise);
		default: {
			return executeDefaultVerwendungsnachweisFormulas(application, kostenKategories, leistungsgruppe);
		}
	}
};

export const executeDefaultVerwendungsnachweisFormulas = (application: any, kostenKategories: any, leistungsgruppe: any) => {
	let appCopy = cloneDeep(application);
	let result: any = [];
	const kostenKategoriesGroup = groupKostenByPosition(kostenKategories);

	// PERSONAL KOSTEN
	if (get(kostenKategoriesGroup, KOSTEN_POSITION.Ausgabe, []).includes(KOSTEN_ARTEN.Personalkosten)) {
		let calculatedPersonalKosten = calculatePersonalKosten(appCopy, leistungsgruppe);
		result.push(...calculatedPersonalKosten);
		for (let ele of calculatedPersonalKosten) {
			set(appCopy, get(ele, 'path'), get(ele, 'value'));
		}
	}

	// SACH KOSTEN
	if (get(kostenKategoriesGroup, KOSTEN_POSITION.Ausgabe, []).includes(KOSTEN_ARTEN.Sachkosten)) {
		let calculatedSachKosten = calculateSachKosten(appCopy);
		result.push(...calculatedSachKosten);
		for (let ele of calculatedSachKosten) {
			set(appCopy, get(ele, 'path'), get(ele, 'value'));
		}
	}

	let calculatedGesamt = calculateGesamt(appCopy);
	result.push(...calculatedGesamt);
	for (let ele of calculatedGesamt) {
		set(appCopy, get(ele, 'path'), get(ele, 'value'));
	}

	const zahlungsWeise = get(appCopy, VN_P.Foerderantrag.schema.attributes.Foerderprogramm.schema.attributes.Konfiguration_Zahlungsweise.path);

	if (zahlungsWeise === ZAHLUNGSWEISE.Nachschus) {
		let calculatedAbrufDaten = calculateAbruf(appCopy);
		result.push(...calculatedAbrufDaten);
		for (let ele of calculatedAbrufDaten) {
			set(appCopy, get(ele, 'path'), get(ele, 'value'));
		}
	}

	return result;
};

function calculatePersonalKosten(application: any, leistungsgruppe: any) {
	let result = [];
	const antragsFormular = get(application, VN_P.Foerderantrag.schema.attributes.Allgemein_Antragsformular.path);
	const allNachweisPositions = get(application, VN_P.Verwendungsnachweis_Nachweispositions.path, []);

	for (let i = 0; i < allNachweisPositions.length; i++) {
		const eachNachweis = allNachweisPositions[i];

		if (get(eachNachweis, NP_P.Allgemein_Kostenposition.schema.attributes.Allgemein_Kostenart_Finanzierung.path) === KOSTEN_ARTEN.Personalkosten) {
			let KalkulationPersonalKosten = {};
			switch (antragsFormular) {
				case ANTRAGS_FORMULAREN.EmobFundE:
					KalkulationPersonalKosten = transformKalkulationBlock(eachNachweis, leistungsgruppe);
					break;
				case ANTRAGS_FORMULAREN.Kreativwirtschaft:
					KalkulationPersonalKosten = transformKalkulationBlockKreativ(eachNachweis, leistungsgruppe);
					break;
				default:
					KalkulationPersonalKosten = transformKalkulationBlock(eachNachweis, leistungsgruppe);
					break;
			}

			result.push({
				path: `${VN_P.Verwendungsnachweis_Nachweispositions.path}.${i}`,
				value: KalkulationPersonalKosten,
			});
		}
	}
	return result;
}
function calculateSachKosten(application: any) {
	let result = [];
	const allNachweisPositions = get(application, VN_P.Verwendungsnachweis_Nachweispositions.path, []);
	for (let i = 0; i < allNachweisPositions.length; i++) {
		const eachNachweis = allNachweisPositions[i];

		if (get(eachNachweis, NP_P.Allgemein_Kostenposition.schema.attributes.Allgemein_Kostenart_Finanzierung.path) === KOSTEN_ARTEN.Sachkosten) {
			const KalkulationSachKosten = transformSachkostenData(eachNachweis);

			result.push({
				path: `${VN_P.Verwendungsnachweis_Nachweispositions.path}.${i}`,
				value: KalkulationSachKosten,
			});
		}
	}

	return result;
}
function calculateGesamt(application: any) {
	let result: any = [];

	const nachgewieseneKostenPath = VN_P.Allgemein_Nachgewiesene_Kosten.path;
	const allNachweisPositions = get(application, VN_P.Verwendungsnachweis_Nachweispositions.path, []);

	const gesamtAusgabe = allNachweisPositions
		.filter((ele: any) => get(ele, NP_P.Allgemein_Kostenart.path) === KOSTEN_POSITION.Ausgabe)
		.reduce((sum: any, eachNachweis: any) => sum + parseFloat(get(eachNachweis, NP_P.Allgemein_Ausgaben_Kosten.path, '0')), 0);
	// const gesamtEinnahme = allNachweisPositions
	// 	.filter((ele: any) => get(ele, NP_P.Allgemein_Kostenart.path) === KOSTEN_POSITION.Einnahme)
	// 	.reduce((sum: any, eachNachweis: any) => sum + parseFloat(get(eachNachweis, NP_P.Allgemein_Ausgaben_Kosten.path, '0')), 0);

	const nachgewieseneKosten = gesamtAusgabe > 0 ? gesamtAusgabe : 0;

	result.push({
		path: nachgewieseneKostenPath,
		value: !isNaN(nachgewieseneKosten) ? nachgewieseneKosten.toFixed(2) : 0,
	});

	return result;
}
function calculateAbruf(application: any) {
	let result: any = [];

	const allNachweisPositions = get(application, VN_P.Verwendungsnachweis_Nachweispositions.path, []);
	const summeAusgaben = Number(get(application, VN_P.Allgemein_Nachgewiesene_Kosten.path, 0));
	const summeEinnahmen = allNachweisPositions
		.filter((eachNachweis: any) => get(eachNachweis, NP_P.Allgemein_Kostenart.path) === KOSTEN_POSITION.Einnahme)
		.reduce((sum: any, eachNachweis: any) => {
			return sum + parseFloat(get(eachNachweis, NP_P.Allgemein_Ausgaben_Kosten.path, '0'));
		}, 0);

	const kalkulationBlock = get(application, VN_P.Block_1.path, []);
	const calculateKalkulationBlock_New = transformAnrufKalkulationBlock(kalkulationBlock, summeAusgaben);

	const getaetigteAusgabeGesamt = calculateKalkulationBlock_New.reduce((sum: any, ele: any) => sum + parseFloat(get(ele, 'Zelle_3', '0')), 0);
	const zuwendungAnteilingGesamt = calculateKalkulationBlock_New.reduce((sum: any, ele: any) => sum + parseFloat(get(ele, 'Zelle_4', '0')), 0);

	const IndividuelleFoerderquoteGesamt = (zuwendungAnteilingGesamt / getaetigteAusgabeGesamt) * 100;

	const berechnungAnteiligeZuwendung = summeEinnahmen > summeAusgaben ? 0 : summeAusgaben - summeEinnahmen;

	const Anforderungsbetrag =
		berechnungAnteiligeZuwendung - parseFloat(get(application, VN_P.Ermittlung_Des_Auszahlun_0254424978_ngen_Im_Gleichen_Projektjahr.path, '0'));

	result.push(
		// Förderquote Ausgaben Block 2
		{
			path: VN_P.Block_1.path,
			value: calculateKalkulationBlock_New,
		},
		// Förderquote Ausgaben (Förderquote Gesamt)
		{
			path: VN_P.Kalkulation_Mittelabruf_Foerderquote_47_Ausgaben_Block_2_Zelle_2.path,
			value: !isNaN(IndividuelleFoerderquoteGesamt) ? IndividuelleFoerderquoteGesamt.toFixed(2) : 0,
		},
		// Förderquote Ausgaben (Tatsächlich getätigte Ausgaben Gesamt)
		{
			path: VN_P.Kalkulation_Mittelabruf_Foerderquote_47_Ausgaben_Block_2_Zelle_3.path,
			value: !isNaN(getaetigteAusgabeGesamt) ? getaetigteAusgabeGesamt.toFixed(2) : 0,
		},
		// Förderquote Ausgaben (Zuwendung anteilig Gesamt)
		{
			path: VN_P.Kalkulation_Mittelabruf_Foerderquote_47_Ausgaben_Block_2_Zelle_4.path,
			value: !isNaN(zuwendungAnteilingGesamt) ? zuwendungAnteilingGesamt.toFixed(2) : 0,
		},
		// Ermittlungs Beträge
		// Bewilligte Zuwendung wird beim erstellen schon festgelegt. Braucht man hier nicht nochmal zu berechnen
		// Zuwendungsfähige Ausgaben
		{
			path: VN_P.Ermittlung_Des_Auszahlungsbetrages_Zuwendungsfaehige_Ausgaben.path,
			value: !isNaN(getaetigteAusgabeGesamt) ? getaetigteAusgabeGesamt.toFixed(2) : 0,
		},
		// Zuwendungs anteilig
		{
			path: VN_P.Ermittlung_Des_Auszahlungsbetrages_Zuwendung_Anteilig.path,
			value: !isNaN(berechnungAnteiligeZuwendung) ? berechnungAnteiligeZuwendung.toFixed(2) : 0,
		},
		// abzüglich bereits abgerufenen Zuwendungen im gleichen Projektjahr wird beim erstellen schon festgelegt. Braucht man hier nicht nochmal zu berechnen
		// Anforderungsbetrag
		{
			path: VN_P.Ermittlung_Des_Auszahlungsbetrages_Anforderungsbetrag.path,
			value: !isNaN(Anforderungsbetrag) ? Anforderungsbetrag.toFixed(2) : 0,
		}
	);
	return result;
}

/*
 ***************************************************************************************************************
 * ************************ HELPER FUNCTIONS *******************************************************************
 * *************************************************************************************************************
 */
const calculateStundenNachweisProMonatGlobal = (stundenNachweisBlock: any, stundenSatz: number) => {
	return stundenNachweisBlock.map((eachMonat: any, i: number) => {
		const einsatzStunden = get(eachMonat, 'Einsatzstunden') ? parseFloat(get(eachMonat, 'Einsatzstunden', '0')) : 0;
		let result = {
			Monat: MONAT_LISTE[i].Monat,
			Einsatzstunden: einsatzStunden,
			Betrag_40836441: einsatzStunden > 0 ? einsatzStunden * stundenSatz : 0,
		};
		if (get(eachMonat, 'id')) set(result, 'id', get(eachMonat, 'id'));
		return result;
	});
};
function transformKalkulationBlock(eachNachweisData: any, _leistungsgruppe: any) {
	const calculateStundenSatz = (kalkulationBlock: any) => {
		const Gehalt = get(
			kalkulationBlock,
			getFieldNamefromDataPath(NP_P.Kalkulation_Personalausgaben_Elektromobilitaet.schema.attributes.Gehalt_Bzw_Lohnbrutto_Monat.path),
			0
		);
		const EinsatzMonate = get(
			kalkulationBlock,
			getFieldNamefromDataPath(NP_P.Kalkulation_Personalausgaben_Elektromobilitaet.schema.attributes.Einsatzmonate_Im_Projekt.path),
			0
		);
		const ProjektAusgabe = parseFloat(Gehalt) * parseFloat(EinsatzMonate);

		const StundenProWoche = get(
			kalkulationBlock,
			getFieldNamefromDataPath(NP_P.Kalkulation_Personalausgaben_Elektromobilitaet.schema.attributes.Std_Woche_Arbeitsvertrag.path),
			0
		);
		const StundenProTag = StundenProWoche / 5;
		const maxEinsatzstage = parseFloat(EinsatzMonate) * 17.5;
		const maxEinsatzstunden = maxEinsatzstage * StundenProTag;
		const Stundensatz = maxEinsatzstunden > 0 ? parseFloat((ProjektAusgabe / maxEinsatzstunden).toFixed(2)) : 0;

		return {
			...kalkulationBlock,
			Projektbezogene_Personalausgaben: !isNaN(ProjektAusgabe) ? ProjektAusgabe.toFixed(2) : 0,
			Std_Tag_Arbeitsvertrag: !isNaN(StundenProTag) ? StundenProTag.toFixed(2) : 0,
			Max_Einsatztage: !isNaN(maxEinsatzstage) ? maxEinsatzstage.toFixed(2) : 0,
			Max_Einsatzstunden: !isNaN(maxEinsatzstunden) ? maxEinsatzstunden.toFixed(2) : 0,
			Stundensatz: !isNaN(Stundensatz) ? Stundensatz.toFixed(2) : 0,
		};
	};

	// calculated data
	const calculatedKalkulationBlock = calculateStundenSatz(get(eachNachweisData, NP_P.Kalkulation_Personalausgaben_Elektromobilitaet.path, {}));
	const calculatedStundenNachweis = calculateStundenNachweisProMonatGlobal(
		get(eachNachweisData, NP_P.Stundennachweis_Geleistete_Stunden.path, []),
		calculatedKalkulationBlock.Stundensatz
	);
	const calculatedGesamtStunde = calculatedStundenNachweis.reduce((sum: any, item: any) => sum + parseFloat(get(item, 'Einsatzstunden', '0')), 0);
	const calculatedGesamtSumme = calculatedStundenNachweis.reduce((sum: any, item: any) => sum + parseFloat(get(item, 'Betrag_40836441', '0')), 0);

	return {
		...eachNachweisData,
		attributes: {
			...get(eachNachweisData, 'attributes', {}),
			Kalkulation_Personalausgaben_Elektromobilitaet: calculatedKalkulationBlock,
			Stundennachweis_Stundensatz: calculatedKalkulationBlock.Stundensatz,
			Stundennachweis_Geleistete_Stunden: calculatedStundenNachweis,
			Stundennachweis_Gesamtstunden: !isNaN(calculatedGesamtStunde) ? calculatedGesamtStunde.toFixed(2) : 0,
			Stundennachweis_Gesamtsumme: !isNaN(calculatedGesamtSumme) ? calculatedGesamtSumme.toFixed(2) : 0,
			Allgemein_Ausgaben_Kosten: !isNaN(calculatedGesamtSumme) ? calculatedGesamtSumme.toFixed(2) : 0,
		},
	};
}
function transformKalkulationBlockKreativ(eachNachweisData: any, leistungsgruppe: any[]) {
	const selectedEigenLeistung = get(
		eachNachweisData,
		NP_P.Kalkulation_Personalausgaben_Kreativwirtschaft.schema.attributes.Kreativ_Art_Der_Eigenleistung.path,
		''
	);

	const calculateKalkulationBlock = (KalkulationBlockData: any) => {
		if (
			selectedEigenLeistung === ART_DER_EIGENLEISTUNG.BareEigenleistung ||
			selectedEigenLeistung === ART_DER_EIGENLEISTUNG.SozialversicherungspflichtigesPersonal
		) {
			const selectedLeistungsGruppeID = getIDFromResponseOption(
				get(KalkulationBlockData, VN_Kalkulation_Personalausgaben_Kreativwirtschaft_COMPONENT_PATHS.Leistungsgruppe)
			);
			const selectedLeistungsGruppe_Obj = leistungsgruppe.find((ele: any) => get(ele, 'id') === selectedLeistungsGruppeID);
			let stundensatz = 0;
			const eingegebeStundenProWoche = get(
				KalkulationBlockData,
				VN_Kalkulation_Personalausgaben_Kreativwirtschaft_COMPONENT_PATHS.StundenProWoche,
				0
			);
			const eingegebeneStellenanteil = get(
				KalkulationBlockData,
				VN_Kalkulation_Personalausgaben_Kreativwirtschaft_COMPONENT_PATHS.StellanteilImVorhaben,
				0
			);
			const eingegebeneEinsatzMonate = get(
				KalkulationBlockData,
				VN_Kalkulation_Personalausgaben_Kreativwirtschaft_COMPONENT_PATHS.Einsatzmonate,
				0
			);

			if (eingegebeStundenProWoche < 38.5) {
				stundensatz = get(selectedLeistungsGruppe_Obj, LG_P.Allgemein_Personalkosten_0221163365_ten_pro_Stunde_385_Std_Woche.path, 0);
			} else {
				stundensatz = get(selectedLeistungsGruppe_Obj, LG_P.Allgemein_Personalkosten_Inkl_Arbeitsplatzkosten_pro_Stunde.path, 0);
			}
			const ErrechnetteMaxEinsatzStunden = 1592 * (eingegebeStundenProWoche / 40) * (eingegebeneEinsatzMonate / 12);
			const ErrechnettePauschale = stundensatz * ErrechnetteMaxEinsatzStunden * (eingegebeneStellenanteil / 100);
			return {
				...KalkulationBlockData,
				[VN_Kalkulation_Personalausgaben_Kreativwirtschaft_COMPONENT_PATHS.Leistungsgruppe]: selectedLeistungsGruppe_Obj || null,
				[VN_Kalkulation_Personalausgaben_Kreativwirtschaft_COMPONENT_PATHS.StundenSatz]: stundensatz,
				Stundensatz: stundensatz,
				[VN_Kalkulation_Personalausgaben_Kreativwirtschaft_COMPONENT_PATHS.ErrechneteMaxEinsatzStunden]: ErrechnetteMaxEinsatzStunden,
				[VN_Kalkulation_Personalausgaben_Kreativwirtschaft_COMPONENT_PATHS.ErrechnetePauschale]: ErrechnettePauschale,
			};
		} else {
			return {
				...KalkulationBlockData,
				Stundensatz: get(KalkulationBlockData, VN_Kalkulation_Personalausgaben_Kreativwirtschaft_COMPONENT_PATHS.StundenSatz),
			};
		}
	};

	const calculatedKalkulationBlock = calculateKalkulationBlock(get(eachNachweisData, NP_P.Kalkulation_Personalausgaben_Kreativwirtschaft.path, {}));
	const calculatedStundenNachweisBlock = calculateStundenNachweisProMonatGlobal(
		get(eachNachweisData, NP_P.Stundennachweis_Geleistete_Stunden.path, []),
		calculatedKalkulationBlock.Stundensatz
	);
	const calculatedGesamtStunde = calculatedStundenNachweisBlock.reduce(
		(sum: any, item: any) => sum + parseFloat(get(item, 'Einsatzstunden', '0')),
		0
	);
	const calculatedGesamtSumme = calculatedStundenNachweisBlock.reduce(
		(sum: any, item: any) => sum + parseFloat(get(item, 'Betrag_40836441', '0')),
		0
	);
	return {
		...eachNachweisData,
		attributes: {
			...get(eachNachweisData, 'attributes', {}),
			Kalkulation_Personalausgaben_Kreativwirtschaft: calculatedKalkulationBlock,
			Stundennachweis_Stundensatz: calculatedKalkulationBlock.Stundensatz,
			Stundennachweis_Geleistete_Stunden: calculatedStundenNachweisBlock,
			Stundennachweis_Gesamtstunden: !isNaN(calculatedGesamtStunde) ? calculatedGesamtStunde.toFixed(2) : 0,
			Stundennachweis_Gesamtsumme: !isNaN(calculatedGesamtSumme) ? calculatedGesamtSumme.toFixed(2) : 0,
			Allgemein_Ausgaben_Kosten: !isNaN(calculatedGesamtSumme) ? calculatedGesamtSumme.toFixed(2) : 0,
		},
	};
}
function transformSachkostenData(eachNachweisData: any) {
	const investition = get(eachNachweisData, NP_P.Beleg_Af_A_Absetzung_Fuer_Abnutzungen_Investition.path, 0);
	const abschreibunsMonate = get(eachNachweisData, NP_P.Beleg_Af_A_Absetzung_Fuer_Abnutzungen_Abschreibungsmonate.path, 0);
	const nutzungsMonate = get(eachNachweisData, NP_P.Beleg_Af_A_Absetzung_Fue_0114037618_m_Betrachteten_Haushaltsjahr.path, 0);
	const projektbezogeneNutzung = get(eachNachweisData, NP_P.Beleg_Af_A_Absetzung_Fue_0188964903_ezogeneggf_Anteilige_Nutzung.path, 0);

	let ansetzbarerBetrag: number = 0.0;

	if (isTrue(get(eachNachweisData, NP_P.Beleg_Af_A_Absetzung_Fuer_Abnutzungen_Abschreibungsfaehig.path))) {
		ansetzbarerBetrag = (investition / abschreibunsMonate) * (nutzungsMonate * (projektbezogeneNutzung / 100));
	} else {
		ansetzbarerBetrag = parseFloat(get(eachNachweisData, NP_P.Allgemein_Ausgaben_Kosten.path, '0.0'));
	}

	return {
		...eachNachweisData,
		attributes: {
			...get(eachNachweisData, 'attributes', {}),
			Beleg_Af_A_Absetzung_Fue_0246879351_etzbarer_Abschreibungsbetrag: !isNaN(ansetzbarerBetrag) ? ansetzbarerBetrag.toFixed(2) : 0,
			Allgemein_Ausgaben_Kosten: !isNaN(ansetzbarerBetrag) ? ansetzbarerBetrag.toFixed(2) : 0,
		},
	};
}
function transformAnrufKalkulationBlock(data: any[], nachgewieseneKosten: number) {
	return data.map((eachEle: any) => {
		const foerderQuote = get(eachEle, 'Zelle_2', 0);
		const getaetigteAusgabe = get(eachEle, 'Zelle_3', nachgewieseneKosten);
		const zuwendungAnteilig = getaetigteAusgabe > 0 ? (getaetigteAusgabe * foerderQuote) / 100 : 0;

		return {
			...eachEle,
			Zelle_3: Number(getaetigteAusgabe).toFixed(2),
			Zelle_4: Number(zuwendungAnteilig).toFixed(2),
		};
	});
}
